import type { FC, PropsWithChildren } from 'react';
import { createContext, useContext, useMemo } from 'react';

import { useCurrentUserStore } from 'store/currentUser';
import { useDebugStore } from 'store/debug';

import type { CommonMetricsParams } from 'YM/Rules/general';

import { EVoxRoles } from 'providers/declare';

import { getDevicesPermissionMap } from 'utils/getDevicesPermissionMap';

import type { TriggerYMParams } from '..';
import { triggerYM } from '..';

export interface IYMContext {
    /** Отправка события яндекс метрики */
    triggerYM: <T>(params: TriggerYMParams<T>) => void;
}

const defaultContext: IYMContext = {
    triggerYM: () => undefined,
};

const Context = createContext<IYMContext>(defaultContext);

export const useYM = () => useContext(Context);

/** Контекст для яндекс метрики */
const YMContextProvider: FC<PropsWithChildren> = ({ children }) => {
    const { currentUser } = useCurrentUserStore();
    const { isDebugMode } = useDebugStore();

    const context = useMemo<IYMContext>(() => {
        return {
            triggerYM: async ({ data, goal }) => {
                const { camera, microphone } = await getDevicesPermissionMap();

                if (isDebugMode) {
                    return Promise.reject(new Error('debug mode enabled'));
                }

                return triggerYM<CommonMetricsParams>({
                    data: {
                        meeting_role: currentUser?.role ?? EVoxRoles.GUEST,
                        setting_permission_camera: camera,
                        setting_permission_mic: microphone,
                        ...data,
                    },
                    goal,
                });
            },
        };
    }, [currentUser?.role]);

    return <Context.Provider value={context}>{children}</Context.Provider>;
};

export default YMContextProvider;
